import type { AirportInfo, AfricanAirportsData } from "@/types/airport.types";

/**
 * Normalize city name for matching (case-insensitive, trim whitespace)
 */
export function normalizeCityName(city: string): string {
  return city.trim().toLowerCase();
}

/**
 * Find airport data for a given city name
 * Returns the first matching airport (preferring large/medium airports)
 */
export function findAirportByCity(
  cityName: string,
  airportsData: AfricanAirportsData
): AirportInfo | null {
  const normalized = normalizeCityName(cityName);

  // Search through all countries
  for (const country in airportsData) {
    const airports = airportsData[country];

    // First pass: look for exact match
    for (const airport of airports) {
      if (normalizeCityName(airport.city) === normalized) {
        return airport;
      }
    }
  }

  // Second pass: look for partial match (city name contains or is contained)
  for (const country in airportsData) {
    const airports = airportsData[country];

    for (const airport of airports) {
      const airportCity = normalizeCityName(airport.city);
      if (
        airportCity.includes(normalized) ||
        normalized.includes(airportCity)
      ) {
        // Prefer larger airports
        if (
          airport.type === "large_airport" ||
          airport.type === "medium_airport"
        ) {
          return airport;
        }
      }
    }
  }

  // Third pass: return any partial match
  for (const country in airportsData) {
    const airports = airportsData[country];

    for (const airport of airports) {
      const airportCity = normalizeCityName(airport.city);
      if (
        airportCity.includes(normalized) ||
        normalized.includes(airportCity)
      ) {
        return airport;
      }
    }
  }

  return null;
}

/**
 * Find country name for a given city by looking up airport data
 * Returns the country name if found, null otherwise
 */
export function findCountryByCity(
  cityName: string,
  airportsData: AfricanAirportsData
): string | null {
  const normalized = normalizeCityName(cityName);

  for (const country in airportsData) {
    const airports = airportsData[country];

    for (const airport of airports) {
      if (normalizeCityName(airport.city) === normalized) {
        return country;
      }
    }
  }

  for (const country in airportsData) {
    const airports = airportsData[country];

    for (const airport of airports) {
      const airportCity = normalizeCityName(airport.city);
      if (
        airportCity.includes(normalized) ||
        normalized.includes(airportCity)
      ) {
        if (
          airport.type === "large_airport" ||
          airport.type === "medium_airport"
        ) {
          return country;
        }
      }
    }
  }

  for (const country in airportsData) {
    const airports = airportsData[country];

    for (const airport of airports) {
      const airportCity = normalizeCityName(airport.city);
      if (
        airportCity.includes(normalized) ||
        normalized.includes(airportCity)
      ) {
        return country;
      }
    }
  }

  return null;
}

/**
 * Get all airports for a specific country
 */
export function getAirportsByCountry(
  countryName: string,
  airportsData: AfricanAirportsData
): AirportInfo[] {
  const normalizedCountry = normalizeCityName(countryName);

  for (const country in airportsData) {
    if (normalizeCityName(country) === normalizedCountry) {
      return airportsData[country];
    }
  }

  return [];
}

